import type { ChangeEvent } from 'react';
import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useCallback, useEffect, useRef, useState,
} from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { IS_TOUCH_ENV } from '../../util/browser/windowEnvironment';
import { pick } from '../../util/iteratees';

import useLastCallback from '../../hooks/useLastCallback';

import Button from '../ui/Button';
import Icon from '../common/icons/Icon';
import InputText from '../ui/InputText';
import Loading from '../ui/Loading';

import './AuthSsgLogin.scss';

type StateProps = Pick<GlobalState, (
  'connectionState' | 'authState' |
  'authIsLoading' | 'authErrorKey'
)>;

/** 账号：4-16 位，字母/数字/下划线，须同时包含字母和数字 */
const ACCOUNT_CHARS_RE = /^[A-Za-z0-9_]{4,16}$/;
const ACCOUNT_HAS_LETTER = /[A-Za-z]/;
const ACCOUNT_HAS_DIGIT = /[0-9]/;
/** 注册密码：8-16 位，仅允许英文字母、数字，须同时包含字母和数字 */
const REGISTER_PASSWORD_CHARS_RE = /^[A-Za-z0-9]{8,16}$/;
const PASSWORD_HAS_LETTER = /[A-Za-z]/;
const PASSWORD_HAS_DIGIT = /[0-9]/;
const ACCOUNT_INPUT_RE = /^[a-zA-Z0-9_]*$/;
const PASSWORD_INPUT_RE = /^[a-zA-Z0-9]*$/;

/** 登录密码：非空且至少 4 位 */
const LOGIN_MIN_PASSWORD_LEN = 4;

function isAccountValid(a: string): boolean {
  if (!a || a.length < 4 || a.length > 16) return false;
  if (!ACCOUNT_CHARS_RE.test(a)) return false;
  return ACCOUNT_HAS_LETTER.test(a) && ACCOUNT_HAS_DIGIT.test(a);
}

/** 注册密码：8-16 位，须同时包含字母和数字 */
function isPasswordValidForRegister(p: string): boolean {
  if (!p || p.length < 8 || p.length > 16) return false;
  if (!REGISTER_PASSWORD_CHARS_RE.test(p)) return false;
  return PASSWORD_HAS_LETTER.test(p) && PASSWORD_HAS_DIGIT.test(p);
}

const AuthPhoneNumber: FC<StateProps> = ({
  connectionState,
  authState,
  authIsLoading,
  authErrorKey,
}) => {
  const {
    ssgSignIn,
    ssgGoToRegister,
    clearAuthErrorKey,
  } = getActions();

  // eslint-disable-next-line no-null/no-null
  const accountRef = useRef<HTMLInputElement>(null);
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [forceAuthReady, setForceAuthReady] = useState(false);

  const isAuthReady = authState === 'authorizationStateWaitPhoneNumber' || forceAuthReady;

  // 退出登录后进入注册页时自动刷新，达到 F5 效果
  useEffect(() => {
    if (authState === 'authorizationStateWaitPhoneNumber') {
      try {
        if (window.sessionStorage.getItem('ssgram_just_logged_out') === '1') {
          window.sessionStorage.removeItem('ssgram_just_logged_out');
          window.location.reload();
        }
      } catch {
        // ignore
      }
    }
  }, [authState]);

  useEffect(() => {
    if (!IS_TOUCH_ENV && accountRef.current) {
      accountRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (authErrorKey) {
      const nextError = typeof authErrorKey === 'string'
        ? authErrorKey
        : '登录失败，请重试';
      setError(nextError);
    }
  }, [authErrorKey]);

  // 长时间没有收到 auth 状态时，避免登录页一直只显示全屏 Loading
  // 注意：无论连接是否就绪都需触发，否则 connectionState 卡在 connecting 时会无限转圈
  useEffect(() => {
    if (isAuthReady || forceAuthReady) return;

    const timer = setTimeout(() => {
      setForceAuthReady(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, [isAuthReady, forceAuthReady]);

  const handleAccountChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val && !ACCOUNT_INPUT_RE.test(val)) return;
    setAccount(val);
    if (error) {
      setError(undefined);
      clearAuthErrorKey();
    }
  });

  const handlePasswordChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val && !PASSWORD_INPUT_RE.test(val)) return;
    setPassword(val);
    if (error) {
      setError(undefined);
      clearAuthErrorKey();
    }
  });

  const handleAccountKeyDown = useLastCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = (e as any).key;
    if (typeof key !== 'string') return;
    if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !/^[a-zA-Z0-9_]$/.test(key)) {
      e.preventDefault();
    }
  });

  const handlePasswordKeyDown = useLastCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = (e as any).key;
    if (typeof key !== 'string') return;
    if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !/^[a-zA-Z0-9]$/.test(key)) {
      e.preventDefault();
    }
  });

  const handlePaste = useLastCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const input = e.currentTarget;
    const id = input.id;
    if (id === 'sign-in-account') {
      if (!ACCOUNT_INPUT_RE.test(text)) {
        e.preventDefault();
        const filtered = text.replace(/[^a-zA-Z0-9_]/g, '');
        if (filtered) setAccount((prev) => (prev + filtered).slice(0, 16));
      }
    } else if (id === 'sign-in-password') {
      if (!PASSWORD_INPUT_RE.test(text)) {
        e.preventDefault();
        const filtered = text.replace(/[^a-zA-Z0-9]/g, '');
        if (filtered) setPassword((prev) => (prev + filtered).slice(0, 16));
      }
    }
  });

  const togglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((prev) => !prev);
  }, []);

  /** 登录：账号须含字母+数字，密码非空且至少 4 位 */
  function validateLogin(): boolean {
    const a = account.trim();
    const p = password.trim();
    if (!a) {
      setError('请输入账号');
      return false;
    }
    if (!isAccountValid(a)) {
      setError('账号须同时包含字母和数字，长度 4-16 位');
      return false;
    }
    if (!p) {
      setError('请输入密码');
      return false;
    }
    if (p.length < LOGIN_MIN_PASSWORD_LEN) {
      setError('密码至少 4 位');
      return false;
    }
    return true;
  }

  /** 注册：账号须含字母+数字，密码 8-16 位且须含字母+数字 */
  function validateRegister(): boolean {
    const a = account.trim();
    if (!a) {
      setError('请输入账号');
      return false;
    }
    if (!isAccountValid(a)) {
      setError('账号须同时包含字母和数字，长度 4-16 位');
      return false;
    }
    if (!password) {
      setError('请输入密码');
      return false;
    }
    if (!isPasswordValidForRegister(password)) {
      setError('密码须同时包含字母和数字，长度 8-16 位');
      return false;
    }
    return true;
  }

  const handleLogin = useLastCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (authIsLoading) return;
    if (!validateLogin()) return;
    ssgSignIn({ account: account.trim(), password: password.trim() });
  });

  const handleRegister = useLastCallback(() => {
    if (!validateRegister()) return;
    ssgGoToRegister({ account: account.trim(), password });
  });

  return (
    <div id="auth-phone-number-form" className="custom-scroll">
      <div className="auth-form">
        <div id="logo" />
        <h1>SSGram</h1>
        <p className="note">输入账号和密码登录或注册</p>
        <form className="form" action="" onSubmit={handleLogin}>
          <InputText
            ref={accountRef}
            id="sign-in-account"
            label="账号（4-16 位，须同时包含字母和数字）"
            value={account}
            error={error}
            maxLength={16}
            autoComplete="username"
            onChange={handleAccountChange}
            onKeyDown={handleAccountKeyDown}
            onPaste={handlePaste}
          />
          <div className="ssg-password-wrapper">
            <InputText
              id="sign-in-password"
              label="密码（8-16 位，须同时包含字母和数字）"
              value={password}
              type={isPasswordVisible ? 'text' : 'password'}
              maxLength={16}
              className="ssg-password-input"
              autoComplete="current-password"
              onChange={handlePasswordChange}
              onKeyDown={handlePasswordKeyDown}
              onPaste={handlePaste}
            />
            <div
              className="ssg-toggle-password"
              onClick={togglePasswordVisibility}
              role="button"
              tabIndex={0}
              title="切换密码显示"
            >
              <Icon name={isPasswordVisible ? 'eye' : 'eye-crossed'} />
            </div>
          </div>
          {isAuthReady ? (
            <>
              <Button
                type="button"
                size="smaller"
                ripple
                isLoading={authIsLoading}
                loadingNoText
                onClick={handleRegister}
              >
                注册
              </Button>
              <Button
                type="submit"
                size="smaller"
                ripple
                isLoading={authIsLoading}
                loadingNoText
                className="ssg-login-btn"
              >
                登录
              </Button>
            </>
          ) : (
            <Loading />
          )}
        </form>
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): StateProps => {
    return {
      ...pick(global, [
        'connectionState',
        'authState',
        'authIsLoading',
        'authErrorKey',
      ]),
    };
  },
)(AuthPhoneNumber));
